import { Injectable, inject } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { AuthStateModel, OTP_MEANS, UserModel } from "@tsin-core/models/auth.model";
import { AuthenticateAction, GetProfileAction, LoadCommunityPathwayAction, LogoutAction, RefreshTokenAction, SaveCommunityPathwayAction, SetAuthAction, SetError, SetLangAction, ValidateTokenAction } from "@tsin-core/actions/auth.action";
import { AuthService } from "@tsin-core/services/http/auth.service";
import { catchError, tap } from "rxjs/operators";
import { of } from "rxjs";

const noAuth = {
  id: null,
  email: null,
  token: null,
  refreshToken: null,
  isOtpEnabled: false,
  otpMeans: OTP_MEANS.NONE,
  isStaff: false,
  role: null,
  user: null,
};

@State<AuthStateModel>({
  name: 'authState',
  defaults: {
    loading: true,
    auth: noAuth,
    language: 'en',
    error: null,
  }
})

@Injectable()
export class AuthState {

  authService: AuthService = inject(AuthService);

  @Selector()
  static getToken(state: AuthStateModel): string | null {
    return state.auth.token;
  }

  @Selector()
  static getRefreshToken(state: AuthStateModel): string | null {
    return state.auth.refreshToken;
  }

  @Selector()
  static getCurrentUser(state: AuthStateModel): UserModel | null {
    return state.auth.user;
  }

  @Selector()
  static getCurrentLang(state: AuthStateModel): 'en' | 'fr' {
    return state.language;
  }

  @Selector()
  static getUserRoles(state: AuthStateModel): string | null {
    return state.auth.role;
  }

  @Selector()
  static getAuthId(state: AuthStateModel): string | null {
    return state.auth.id;
  }

  @Selector()
  static getIsAuthenticated(state: AuthStateModel): boolean {
    return !!state.auth.token;
  }

  @Selector()
  static isLoading(state: AuthStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: AuthStateModel) {
    return state.error;
  }

  @Action(SetLangAction)
  setLang(ctx: StateContext<AuthStateModel>, action: SetLangAction) {
    ctx.patchState({ language: action.payload });
  }

  @Action(AuthenticateAction)
  validateLogin(ctx: StateContext<AuthStateModel>, action: AuthenticateAction) {
    ctx.patchState({ loading: true });
    return this.authService.validate(action.payload).pipe(
      tap((result: any) => {
        ctx.patchState({
          auth: result,
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        return of(error);
      })
    );
  }

  @Action(ValidateTokenAction)
  validateToken(ctx: StateContext<AuthStateModel>, action: ValidateTokenAction) {
    ctx.patchState({ loading: true });
    return this.authService.validateToken(action.payload.token).pipe(
      tap((result: any) => {
        ctx.patchState({
          auth: {
            id: result.id,
            email: result.email,
            token: action.payload.token,
            refreshToken: action.payload.refreshToken,
            role: result.role,
            user: result,
            isOtpEnabled: false,
            otpMeans: OTP_MEANS.NONE
          },
          loading: false
        });
      }),
      catchError((error) => {
        // // console.log('Validate Token Error', error);
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      })
    );
  }

  @Action(GetProfileAction)
  getProfile(ctx: StateContext<AuthStateModel>, action: GetProfileAction) {
    ctx.patchState({ loading: true });
    return this.authService.getCurrentUser().pipe(
      tap((result: any) => {
        // ctx.patchState({
        //   auth: {
        //     ...ctx.getState().auth,
        //     user: result
        //   },
        //   loading: false
        // });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        return of(error);
      })
    );
  }

  @Action(SetAuthAction)
  setAuth(ctx: StateContext<AuthStateModel>, action: SetAuthAction) {
    const u = action.payload.user;
    ctx.patchState({
      auth: {
        id: u?.id ?? u?._id ?? null,
        email: u?.email ?? null,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        role: u?.role ?? null,
        user: u ?? null,
        isOtpEnabled: false,
        otpMeans: OTP_MEANS.NONE,
      },
      loading: false,
    });
  }

  @Action(LoadCommunityPathwayAction)
  loadCommunityPathway(ctx: StateContext<AuthStateModel>) {
    return this.authService.getCurrentUser().pipe(
      tap((user: UserModel) => this.patchCommunityPathway(ctx, user)),
    );
  }

  @Action(SaveCommunityPathwayAction)
  saveCommunityPathway(ctx: StateContext<AuthStateModel>, action: SaveCommunityPathwayAction) {
    return this.authService.updateCommunityPathway(action.payload).pipe(
      tap((response) => this.patchCommunityPathway(ctx, response.data)),
    );
  }

  /** Merge only the pathway fields so the rest of the signed-in user (e.g. the enriched PRO role) is untouched. */
  private patchCommunityPathway(ctx: StateContext<AuthStateModel>, source: Partial<UserModel> | null | undefined) {
    const { auth } = ctx.getState();
    if (!auth.user || !source) return;
    ctx.patchState({
      auth: {
        ...auth,
        user: {
          ...auth.user,
          communityPathway: source.communityPathway,
          communityPathwayAcknowledgedAt: source.communityPathwayAcknowledgedAt,
        },
      },
    });
  }

  @Action(LogoutAction)
  logOut(ctx: StateContext<AuthStateModel>, action: LogoutAction) {
    ctx.patchState({ loading: true });
    console.log('🔐 Logout: clearing state and localStorage');

    // Clear localStorage tokens so they don't persist across sessions
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');

    ctx.patchState({
      auth: undefined,
      loading: false
    });

    // Clear shared SSO cookie and redirect to centralized auth-ui logout
    const config = (window as any).runtimeConfig;
    const authUiUrl = config?.authUiUrl;
    if (authUiUrl && config?.environment !== 'dev') {
      document.cookie = 'id_token=; path=/; domain=.stage.tsin.ca; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=None';
      const returnTo = window.location.origin + '/account/validate-login';
      window.location.href = `${authUiUrl}/logout?returnTo=${encodeURIComponent(returnTo)}`;
    }
  }

  @Action(RefreshTokenAction)
  refreshToken(ctx: StateContext<AuthStateModel>, action: RefreshTokenAction) {
    ctx.patchState({ loading: true });
    return this.authService.refreshToken(action.payload).pipe(
      tap((result: any) => {
        ctx.patchState({
          auth: {
            ...ctx.getState().auth,
            token: result.accessToken,
          },
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      })
    );
  }

}
